import {
  FaLinkedinIn,
  FaInstagram,
  FaXTwitter,
  FaWhatsapp,
  FaArrowUp,
  FaLocationDot,
} from "react-icons/fa6";
import { NAV_LINKS, WHATSAPP_LINK, CONTACT, FOOTER_COPY } from "@/data/content";
import Logo from "@/components/site/Logo";

const socials = [
  { icon: FaLinkedinIn, href: CONTACT.linkedin, label: "LinkedIn" },
  { icon: FaInstagram, href: CONTACT.instagram, label: "Instagram" },
  { icon: FaXTwitter, href: "https://www.twitter.com", label: "Twitter" },
  { icon: FaWhatsapp, href: WHATSAPP_LINK, label: "WhatsApp" },
];

const Footer = () => {
  const go = (href) => {
    if (window.lenis) {
      window.lenis.scrollTo(href, { duration: 1.0 });
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTop = () => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1.0 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="relative overflow-hidden bg-green-primary text-white">
      <div className="divider-gold" />
      <div className="mx-auto max-w-[1300px] px-6 py-16 md:px-10 md:py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5 flex flex-col items-start justify-start">
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                scrollToTop();
              }}
              className="inline-block focus:outline-none focus:ring-2 focus:ring-gold-primary/50 rounded"
              aria-label="Back to top"
            >
              <Logo dark className="h-10 sm:h-11 md:h-12 w-auto" />
            </a>
            <p className="mt-4 max-w-md text-[15px] font-bold text-gold-primary leading-snug">
              {FOOTER_COPY.tagline}
            </p>
            <p className="mt-2 max-w-md text-[14px] leading-relaxed text-white/85 font-medium">
              {FOOTER_COPY.description}
            </p>
            <div className="mt-7 flex gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="grid h-11 w-11 place-items-center rounded-full border border-gold-soft/40 text-white/90 transition-all duration-300 hover:border-gold-primary hover:bg-gold-primary hover:text-white"
                >
                  <s.icon />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.22em] text-gold-primary">
              Quick Links
            </p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => go(l.href)}
                    className="text-[15px] text-white/80 transition-colors hover:text-gold-primary font-medium"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="mb-5 text-[11px] font-bold uppercase tracking-[0.22em] text-gold-primary">
              Get in Touch
            </p>
            <ul className="space-y-2 text-[14px] text-white/85">
              {CONTACT.phones.map((p) => (
                <li key={p}>
                  <a href={`tel:${p.replace(/\s/g, "")}`} className="hover:text-gold-primary transition-colors font-medium">
                    {p}
                  </a>
                </li>
              ))}
              <li className="pt-2 text-[10px] font-bold uppercase tracking-widest text-gold-primary">
                Jobseekers
              </li>
              {CONTACT.emails.jobseekers.map((e) => (
                <li key={e}>
                  <a href={`mailto:${e}`} className="hover:text-gold-primary transition-colors font-medium">
                    {e}
                  </a>
                </li>
              ))}
              <li className="pt-2 text-[10px] font-bold uppercase tracking-widest text-gold-primary">
                Business & Corporate
              </li>
              {CONTACT.emails.business.map((e) => (
                <li key={e}>
                  <a href={`mailto:${e}`} className="hover:text-gold-primary transition-colors font-medium">
                    {e}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-6 space-y-2 border-t border-gold-soft/30 pt-4">
              <div className="text-[13px] text-white/90">
                <p className="flex items-center gap-2 font-bold text-gold-primary">
                  <FaLocationDot className="text-xs shrink-0" />
                  {CONTACT.office.name}
                </p>
                <p className="mt-1 leading-relaxed text-white/80 pl-4 font-medium">{CONTACT.office.address}</p>
                {CONTACT.office.googleBusinessProfile && (
                  <a
                    href={CONTACT.office.googleBusinessProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 ml-4 inline-flex items-center gap-1.5 text-xs font-bold text-gold-primary hover:underline"
                  >
                    Google Business Profile →
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-5 border-t border-gold-soft/30 pt-8 md:flex-row">
          <p className="text-[13px] text-white/70">
            © {new Date().getFullYear()} PLACE8 Recruitment Consultancy. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-[13px] text-white/70 font-medium">PLACE8 Talent Acquisition</span>
            <button
              onClick={scrollToTop}
              data-testid="footer-back-to-top"
              className="grid h-11 w-11 place-items-center rounded-full btn-gold text-white transition-transform hover:-translate-y-1 shadow-soft"
              aria-label="Back to top"
            >
              <FaArrowUp />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
